import React, { useMemo } from 'react';
import { ChevronDown, RefreshCcw, LucideIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem
} from '@epoch-ui/components/DropdownMenu';
import { GuestStatus } from 'types/guest.types';
import { Button } from '@epoch-ui/components/Button';
import { cn } from '@epoch-ui/lib/utils';

import { getIconForStatus } from '../utils';

export interface RsvpControlDropdownItem {
  icon?: JSX.Element;
  text: string;
  selected?: boolean;
}

export const CHANGE_RSVP_OPTION: RsvpControlDropdownItem = {
  text: 'Change RSVP status',
  icon: <RefreshCcw />
};

interface RsvpControlDropdownData {
  text: string;
  className: string;
  icon?: JSX.Element;
}

const getDropdownDataForStatus = (status: GuestStatus): RsvpControlDropdownData => {
  const StatusIcon: LucideIcon = getIconForStatus(status);
  const icon = <StatusIcon className="tw-text-rsvp-blue" />;
  switch (status) {
    case GuestStatus.CONFIRMED:
      return {
        text: 'Going',
        className: 'tw-text-white tw-bg-rsvp-green',
        icon
      };
    case GuestStatus.INTERESTED:
      return {
        text: 'Maybe',
        className: 'tw-text-white tw-bg-rsvp-blue',
        icon
      };
    case GuestStatus.WAITLISTED:
      return {
        text: 'Waitlisted',
        className: 'tw-text-white tw-bg-accent-purple',
        icon
      };
    case GuestStatus.CANCELLED:
      return {
        text: 'Not going',
        className: 'tw-text-white tw-bg-rsvp-red',
        icon
      };
    case GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY:
      return {
        text: 'Incomplete',
        className: 'tw-text-black tw-bg-rsvp-yellow',
        icon
      };
    case GuestStatus.ACTION_REQUIRED:
      return {
        text: 'Pending',
        className: 'tw-text-black tw-bg-light-grey',
        icon
      };
    case GuestStatus.INVITED:
      return {
        text: 'Invited',
        className: 'tw-text-black tw-bg-light-grey',
        icon
      };
    default:
      return {
        text: 'None',
        className: 'tw-text-black tw-bg-light-grey',
        icon
      };
  }
};

export interface RsvpControlDropdownProps {
  status: GuestStatus;
  onSelect: (option: string) => void;
  disabled?: boolean;
}

export const RsvpControlDropdown: React.FC<React.PropsWithChildren<RsvpControlDropdownProps>> = ({
  status,
  onSelect,
  disabled = false
}) => {
  const dropdownData: RsvpControlDropdownData = useMemo(
    () => getDropdownDataForStatus(status),
    [status]
  );
  const items: RsvpControlDropdownItem[] = useMemo(() => {
    return [
      {
        text: dropdownData.text,
        icon: dropdownData.icon,
        selected: true
      },
      CHANGE_RSVP_OPTION
    ];
  }, [dropdownData]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger disabled={disabled} asChild>
        <Button size="xs" className={cn(dropdownData.className, 'tw-rounded-[8px]')}>
          <span>{dropdownData.text}</span>
          <ChevronDown className="tw-ml-1" size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {items.map((item) => (
          <DropdownMenuItem
            key={item.text}
            className={cn(item.selected && 'tw-text-primary-blue')}
            onClick={(): void => onSelect(item.text)}
          >
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2 [&_svg]:tw-h-4 [&_svg]:tw-w-4">
              {item.icon}
              <div>{item.text}</div>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
